import React, { useState, useRef } from "react";
import "./contentpage.css";

import BoxWithShadow from "../components/BoxWithShadow";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicTradePage9() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-8",
    index: "/index",
    next: "/islamic-trade-page-10",
  };
  const titleText = " Paying in Advance for Future Goods (As-Salam)";
  const questions = [
    {
      text: "1. What is the key characteristic that distinguishes As-Salam contract from a regular sale??",
      options: [
        {
          value: "A",
          label: "A. The buyer receives the goods before paying.",
        },
        {
          value: "B",
          label:
            "B. The goods must be physically handed over during the contract.",
        },
        {
          value: "C",
          label:
            "C. The buyer pays in full at the time of the contract for future delivery.",
        },
        {
          value: "D",
          label: "D. Both parties can delay payment and delivery.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Which of the following conditions is not required for a valid As-Salam contract?",
      options: [
        { value: "A", label: "A. Full payment at the time of contract." },
        { value: "B", label: "B. Specifying the delivery date and place." },
        {
          value: "C",
          label: "C. The goods must already exist at the time of contract.",
        },
        {
          value: "D",
          label: "D. Clear description of goods (quality and quantity)",
        },
      ],
      correct: "C",
    },
    {
      text: "3.  What is the primary wisdom behind requiring the goods in As-Salam contract to be non-specific (i.e., not tied to a particular item)?",
      options: [
        {
          value: "A",
          label: "A. To allow the seller to deliver anything they wish",
        },
        {
          value: "B",
          label:
            "B. To ensure the goods can be replaced if they are damaged before delivery.",
        },
        { value: "C", label: "C. To avoid the need for documentation." },
        {
          value: "D",
          label: "D. To allow bargaining at the time of delivery.",
        },
      ],
      correct: "B",
    },
    {
      text: "4. Which Hadith is most commonly cited to support the permissibility of As-Salam?",
      options: [
        {
          value: "A",
          label:
            "A. The buyer and seller have the option to cancel until they part ways.",
        },
        {
          value: "B",
          label:
            "B. Whoever pays in advance for dates, let him do so for a specified measure and time.",
        },
        {
          value: "C",
          label: "C. Allah has permitted trade and forbidden interest.",
        },
        {
          value: "D",
          label: "D. The hand that gives is better than the hand that takes..",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={9}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 9</b>! Today, we will delve into one of the most
        practical and forward-looking contracts in Islamic commercial law i.e
        As-Salam. This contract allows a buyer to pay in advance for goods that
        will be delivered at a specified future date, making it especially
        useful in agricultural and trade-based economies. We will study the
        definition of As-Salam, explore its legitimacy in light of the Qur’an
        and Sunnah, and examine the conditions required for it to be valid under
        Shariah. You will learn how Islam balances the interests of both parties
        by ensuring clear terms, preventing uncertainty (gharar), and promoting
        ethical trade. By the end of today’s session, you’ll understand how
        As-Salam plays a key role in providing liquidity to producers,
        facilitating commerce, and maintaining justice in deferred delivery
        transactions.
        <br />
        <br />
        <b>Definition:</b> <br />
        <br />
        As-Salam is a type of sale in Islamic jurisprudence where the buyer pays
        the price in advance for goods that are to be delivered at a specified
        future date. This contract is widely practiced in agriculture and trade,
        ensuring mutual benefit for both parties: the buyer secures goods at a
        fixed price, while the seller gains upfront capital.
        <br />
        <br />
        <b>The Foundations and Context of As-Salam</b>
        <br />
        <br /> In the intricate structure of Islamic commercial law, As-Salam
        stands out as one of the most practical, humane, and forward-thinking
        financial instruments. To understand its significance, we must first
        examine the social and economic context in which it was first practiced.
        The contract of As-Salam was introduced during the time of the Prophet
        Muhammad (peace be upon him), when trade, agriculture, and craftsmanship
        formed the lifeblood of the Arabian economy. Farmers and small traders
        often needed liquidity long before their goods were ready for delivery.
        For example, a date farmer might need funds to irrigate and maintain his
        farm months before harvest. However, without external financing options
        that complied with Shariah, these producers were vulnerable to
        exploitation from moneylenders who charged interest (riba), or they had
        to sell their goods at extremely low prices to survive.
        <br />
        As-Salam emerged as a Shariah-compliant solution to this problem,
        designed to meet the liquidity needs of producers while ensuring
        fairness and transparency for buyers. The contract allowed the buyer to
        pay in full at the time of the agreement for goods to be delivered at a
        specified future date. In this way, it was a mechanism that provided
        immediate financial support to the seller, enabling him to continue
        production, while the buyer benefited from a guaranteed future delivery
        at a pre-agreed price. The beauty of this arrangement lies in how it
        balances the principles of justice, mutual benefit, and the elimination
        of uncertainty, which are the bedrock of Islamic trade ethics.
        <br />
        The Qur’an emphasizes the importance of recording contracts and
        maintaining transparency in financial dealings. As stated in the Qur'an
        :<br />
        “O believers! When you contract a loan for a fixed period of time,
        commit it to writing.”{" "}
        <a href="https://quran.com/2/282" target="blank">
          (Surah Al Baqarah 2:282)
        </a>
        <br />
        This verse underscores the importance of clarity in transactions
        involving deferred delivery or payment. Although the verse specifically
        addresses debt contracts, scholars throughout history have extended its
        application to deferred sale contracts such as As-Salam because of their
        structural similarity. Both involve a commitment that spans over time,
        requiring explicit documentation and mutual consent to avoid disputes or
        deception.
        <br />
        The Prophet Muhammad (peace be upon him) also legitimized the contract
        of As-Salam through his sayings and practice. <br />
        Ibn Abbas (may Allah be pleased with him) reported: “The Prophet came to
        Medina, and the people used to pay in advance the price of fruits (to be
        delivered later) for one or two years. The Prophet said: ‘Whoever pays
        money in advance for dates (to be delivered later) should pay it for
        known specified weight and measure (of the dates)’”{" "}
        <a href="https://sunnah.com/bukhari:2239" target="blank">
          (Sahih al-Bukhari 2240)
        </a>
        .<br />
        This hadith is considered the cornerstone of As-Salam , confirming its
        permissibility while laying down its essential conditions i.e clarity in
        quantity, quality, and delivery time.
        <br />
        The wisdom behind this ruling reflects Islam’s concern for protecting
        both parties from harm. The buyer must not suffer loss due to ambiguity,
        and the seller must not face undue hardship due to lack of funds.
        Through As-Salam, Islamic law provides a just alternative to
        interest-bearing loans by turning the transaction into a legitimate sale
        based on mutual benefit. It transforms the concept of financing from one
        rooted in exploitation to one built on cooperation and social justice.
        <br />
        As-Salam also represents Islam’s broader economic philosophy, which
        emphasizes real economic activity rather than speculative or paper-based
        transactions. The essence of trade in Islam is the exchange of value
        that contributes to the welfare of society, whether through goods,
        services, or labor. By requiring that As-Salam be tied to goods with
        clearly defined characteristics, the Shariah ensures that wealth
        creation is linked to productivity and the circulation of real assets,
        not to artificial financial manipulation.
        <br />
        Moreover, the spirit of As-Salam reflects Islam’s balance between mercy
        and discipline. On one hand, it allows flexibility for sellers who need
        advance payment to sustain their livelihoods. On the other, it imposes
        strict conditions to ensure that no form of uncertainty (gharar) or
        injustice creeps into the transaction. This delicate equilibrium between
        compassion for human need and adherence to moral boundaries is what
        gives As-Salam its timeless relevance in both classical and modern
        economies.
        <br />
        <br />
        <b>Legitimacy and Ethical Foundations in Qur’an and Sunnah</b>
        <br />
        <br />
        The permissibility of As-Salam is grounded in the Qur'an and the Sunnah
        of the Prophet Muhammad (peace be upon him), which together form the
        foundation of Islamic commercial ethics. The Qur’anic emphasis on
        recording debts and contracts points to a larger moral vision: that
        wealth and trade should circulate transparently, without ambiguity or
        deceit. The divine injunction in Surah Al-Baqarah (2:282) commands
        believers to document their deferred transactions precisely because
        deferred exchanges inherently carry a risk of dispute if not clarified.
        <br />
        By linking this guidance to the contract of As-Salam, jurists have
        demonstrated that Islam not only tolerates deferred transactions but
        actively encourages their proper regulation for societal benefit. Islam
        does not discourage trade or profit; rather, it sanctifies them when
        conducted ethically. It recognizes that human societies depend on trust,
        credit, and deferred commitments to sustain economic life. What Islam
        forbids is uncertainty, deception, and interest-based exploitation that
        turn economic activity into instruments of injustice.
        <br />
        The hadith narrated by Ibn Abbas is particularly instructive because it
        was revealed in a context where the people of Medina were already
        practicing advance payments for agricultural produce. However, their
        practice lacked clear regulation, which led to disputes and unfair
        outcomes. The Prophet (peace be upon him) did not abolish the practice
        but rather refined it, setting conditions that transformed it into a
        fair and Shariah-compliant transaction. By insisting on specific
        measures, known quantities, and definite timelines, he eliminated the
        elements of gharar that would otherwise render the sale invalid.
        <br />
        This prophetic wisdom exemplifies Islam’s method of reform: rather than
        uprooting human economic habits, it purifies them by aligning them with
        divine principles. As-Salam thus embodies Islam’s broader approach to
        financial justice, it adapts to human necessity while remaining firmly
        within the moral framework of Shariah.
        <br />
        From a moral perspective, the ethical foundation of As-Salam lies in its
        emphasis on mutual trust and responsibility. The buyer, having paid the
        full amount in advance, trusts that the seller will deliver the goods as
        agreed. The seller, having received payment, carries a moral and legal
        obligation to fulfill his commitment. The transaction thus strengthens
        the bond of amanah (trustworthiness), which is a cornerstone of Islamic
        business ethics. The Prophet Muhammad (peace be upon him) said that “The
        truthful, trustworthy merchant is with the Prophets, the truthful, and
        the martyrs.”
        <a href="https://sunnah.com/tirmidhi:1209" target="blank">
          (Jami' at-Tirmidhi 1209)
        </a>
        <br />
        This hadith highlights the spiritual dimension of commercial integrity,
        trade in Islam is not just an economic activity but a form of worship
        when conducted honestly.
        <br />
        By prohibiting riba and gharar while allowing structured contracts like
        As-Salam, Islam fosters a moral economy where trade serves human welfare
        instead of greed. It ensures that the circulation of wealth is rooted in
        effort and mutual consent, not exploitation. The advance payment system,
        when practiced ethically, stimulates production, supports small
        producers, and enhances community welfare.
        <br />
        Furthermore, the legitimacy of As-Salam reflects the Islamic ideal of
        social interdependence. In this contract, both parties need each other
        i.e the seller requires liquidity, and the buyer requires goods. Each
        benefits without oppressing the other. This principle of cooperation
        (ta’awun) distinguishes Islamic finance from interest-based lending
        systems, which often burden one party while enriching the other.
        As-Salam transforms credit relationships into trade relationships,
        aligning economic behavior with spiritual responsibility.
        <br />
        <br />
        <b>Conditions Governing As-Salam</b>
       </p>
        <table className="trade-table">
          <tr>
            <th>Conditions of As-Salam</th>
          </tr>{" "}
          <tr>
            <td>Full payment muste be made in advance</td>
          </tr>
          <tr>
            <td>Attributes of goods must be well defined</td>
          </tr>
          <tr>
            <td>The Date of delivery must be specified</td>
          </tr>
          <tr>
            <td>
              The goods to be delivered must not exist at the time of contract
              agreement
            </td>
          </tr>
          <tr>
            <td>The goods to be delivered must be fungible</td>
          </tr>
        </table>
      <p className="content">
        Let us understand these conditions in more detail.
        <br />
        <br />
        The Shariah prescribes several essential conditions to ensure that
        As-Salam remains fair, transparent, and free from elements of riba or
        gharar. Each condition is rooted in both practical wisdom and ethical
        reasoning, reflecting Islam’s concern for justice in contractual
        relations.
        <br />
        The first condition is that the full payment must be made at the time of
        the contract. This requirement prevents the sale from becoming a
        deferred-deferred exchange, which would create unacceptable uncertainty.
        If both the goods and payment were postponed, neither party would have
        assurance of the other’s performance, and the transaction could devolve
        into speculation rather than legitimate trade. By requiring upfront
        payment, the Shariah anchors the transaction in tangible reality, the
        buyer commits real capital, and the seller undertakes a real obligation
        to deliver goods.
        <br />
        The second condition concerns the description of goods. The goods must
        be clearly defined in terms of type, quality, quantity, and attributes.
        For example, if the contract involves wheat, the variety, grade, and
        weight must be explicitly mentioned. This clarity prevents disputes and
        ensures that both parties share a common understanding of the subject
        matter. In Islamic law, ignorance (jahalah) in contractual terms is one
        of the main causes of gharar. Therefore, As-Salam is valid only when the
        specifications are detailed enough to eliminate uncertainty.
        <br />
        The third condition is that the delivery time and place must be fixed
        and mutually agreed upon. This ensures that both parties know their
        obligations and can plan accordingly. A vague or open-ended delivery
        schedule would reintroduce uncertainty, defeating the purpose of the
        contract.
        <br />
        The fourth condition is that the goods sold under As-Salam do not need
        to exist at the time of the contract. This is one of the features that
        distinguish As-Salam from ordinary sales. The allowance of non-existent
        goods reflects the contract’s purpose as a financing tool rather than a
        speculative one. However, the goods must be such that they can be
        produced and delivered at the specified time; otherwise, the contract
        would be invalid. This provision highlights Islam’s pragmatic
        understanding of economic life, it recognizes the natural delays in
        production while maintaining ethical safeguards.
        <br />
        The fifth condition is the prohibition of singling out a specific item.
        The subject matter of As-Salam must be a generic, fungible good rather
        than a particular asset. For instance, one cannot say, “I sell you the
        dates from this particular tree next year,” because the specific tree
        might die or fail to bear fruit, making fulfillment impossible. Instead,
        the contract must refer to a standard category of goods that can be
        delivered regardless of the fate of individual items. This distinction
        turns the subject matter into a liability (dayn) upon the seller rather
        than a specific object in existence.
        <br />
        These conditions serve a dual purpose: they protect both parties from
        harm and ensure that the contract remains an instrument of fairness, not
        speculation. Every clause in As-Salam is designed to close the door to
        disputes while opening the door to lawful cooperation. The focus on
        clarity and full payment reflects Islam’s profound understanding of
        human behavior, people are less likely to deceive or delay when their
        obligations are transparent and immediate.
        <br />
        In addition to these core requirements, jurists have also established
        secondary rules concerning cancellation, substitution, and delivery
        verification. For example, if the seller fails to deliver on time, the
        buyer may extend the deadline or cancel the contract, but he cannot
        demand interest or additional compensation. This preserves the sanctity
        of the contract and ensures that no riba enters the transaction, even
        indirectly.
        <br />
        <br />
        <b>Economic and Social Implications of As-Salam</b>
        <br />
        <br />
        The contract of As-Salam is not merely a legal mechanism, it carries
        profound economic and social implications that illustrate the genius of
        Islamic financial ethics. At its core, As-Salam serves as a bridge
        between producers in need of liquidity and buyers seeking reliable
        supply, fostering mutual benefit and economic stability.
        <br />
        In agricultural societies, farmers often face seasonal income patterns
        where their expenses precede their revenues. They need funds to buy
        seeds, pay laborers, and maintain their farms, but they will not see
        returns until harvest time. Conventional finance addresses this gap
        through interest-bearing loans, which transfer risk to the borrower and
        often lead to exploitation. As-Salam offers a morally superior
        alternative: instead of lending money for interest, the buyer pays in
        advance for goods that the farmer will produce later. The farmer gains
        liquidity without falling into debt, while the buyer secures future
        goods at a fair price.
        <br />
        This arrangement contributes to the stability of local economies by
        circulating money productively. The advance payment is not idle capital,
        it goes directly into production, stimulating economic growth. As-Salam
        thus exemplifies Islam’s vision of an economy rooted in real activity
        rather than financial speculation.
        <br />
        In modern contexts, the principles of As-Salam can be applied to supply
        chain financing, microfinance, and agricultural cooperatives. For
        example, a cooperative may use As-Salam to provide funds to small
        farmers in exchange for a share of their future produce, ensuring both
        liquidity and fairness.
        <br />
        Moreover, As-Salam promotes social solidarity. It encourages wealthier
        buyers to support producers through prepayment, fostering cooperation
        rather than competition. The contract embodies the Quranic principle
        that wealth should not “circulate only among the rich.” By empowering
        small producers and traders, As-Salam becomes a tool for economic
        justice and inclusive growth.
        <br />
        From an ethical standpoint, As-Salam reinforces trust as the foundation
        of commerce. Both parties enter the agreement with mutual reliance, the
        buyer trusting the seller to deliver as promised, and the seller
        trusting the buyer’s advance payment. This trust-based relationship
        strengthens social cohesion and cultivates moral discipline.
      </p>
      <p className="content">
        <b>Types of Sale Contracts:</b> <br />
        <br />
        Thus far we have studied all four types of possible sale contracts in
        <b> non-usurious</b> goods and thier rulings:
        <br />
        <br />
        <table className="trade-table ">
          <thead>
            <tr>
              <th>Type of Sale</th>
              <th>Ruling</th>
            </tr>
            <tr>
              <td>Immediate payment and immediate possesion of goods</td>
              <td>Permissible ✔</td>
            </tr>
            <tr>
              <td>Deferred payment and deferred possesion of goods</td>
              <td>Impermissible ✘</td>
            </tr>
            <tr>
              <td>Advance payment and deferred posession of goods</td>
              <td>Permissible ✔</td>
            </tr>
            <tr>
              <td>Deferred payment and immediate possesion of goods</td>
              <td>Permissible ✔</td>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
        <br />
        <br />
        <BoxWithShadow>
          <b>IMPORTANT</b>
          <br />
          As-Salam differs from the comtemporary futures contract such that in
          the former the payment is made upfront while the goods are delivered
          at a later specified date, wherease in the latter both the payment and
          the delivery of the goods or services is deffered making it
          impermissible.
        </BoxWithShadow>
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today, we explored the concept of As-Salam, a Shariah-compliant contract
        in which the buyer pays the full price in advance for goods that will be
        delivered at a later date. We learned that this contract is particularly
        beneficial in agriculture and trade, as it provides upfront capital to
        the seller and price certainty to the buyer. We examined the Qur’anic
        basis for deferred contracts and studied a key hadith from the Prophet
        Muhammad (peace be upon him) that outlines the conditions for a valid
        As-Salam contract, including full advance payment, specification of
        quantity and quality, a fixed delivery date, and a prohibition on
        selling specific, individualized items. We also reflected on the wisdom
        behind these rulings, which aim to prevent uncertainty (gharar), protect
        both parties, and ensure transparency and justice in commercial
        dealings.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        As we conclude our study of As-Salam contracts and their role in
        enabling structured and fair trade, tomorrow we will turn our attention
        to the deeply ethical and compassionate institution of Qardh, or loans,
        in Islam. Unlike trade transactions that involve mutual profit, Qardh is
        a selfless act of financial support where the lender expects no material
        gain in return. On Day 10, we will explore how Islam views loans as a
        form of charity, the rulings that govern borrowing and lending, and the
        spiritual rewards promised to those who relieve others of financial
        hardship. We will also learn how trade and loans, though distinct in
        purpose, work together to uphold a balanced and just economy. Finally,
        we will understand why Islam strictly prohibits disguising
        interest-based transactions as trade contracts, emphasizing integrity
        and social equity in all financial dealings.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicTradePage9;
